import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { round_currency } from '@app/shared/util';

@Component({
  selector: 'input-price',
  standalone: true,
  imports: [],
  templateUrl: './input-price.component.html',
  styleUrls: ['./input-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ {   provide: NG_VALUE_ACCESSOR,   useExisting: forwardRef(() => InputPriceComponent),   multi: true } ]
})
export class InputPriceComponent implements ControlValueAccessor {
  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  @Input() isDisabled: boolean = false;
  // Mode standalone : [value]="..." au lieu de formControlName
  @Input() set value(v: number | null) { this.writeValue(v); }
  @Output() priceChange = new EventEmitter<number | null>();

  displayValue = '';
  private rawValue: number | null = null;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: number | null): void {
    this.rawValue = value ?? null;
    const formatted = this.formatDisplay(this.rawValue);
    this.displayValue = formatted;
    this.inputEl?.nativeElement && (this.inputEl.nativeElement.value = formatted);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onFocus(event: FocusEvent): void {
    // const rawStr = this.rawValue != null ? String(this.rawValue) : '';
    // (event.target as HTMLInputElement).value = rawStr;
    (event.target as HTMLInputElement).select();
  }

  onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = this.parse(raw);
    this.rawValue = parsed;
    this.onChange(parsed);
    this.priceChange.emit(parsed);
  }

  onBlur(): void {
    if (this.rawValue !== null) {
      this.rawValue = round_currency(this.rawValue, 4);
      this.onChange(this.rawValue);
    }
    const formatted = this.formatDisplay(this.rawValue);
    this.displayValue = formatted;
    this.inputEl.nativeElement.value = formatted; // bypass Angular change detection
    this.priceChange.emit(this.rawValue); // valeur finale après arrondi
    this.onTouched();
  }

  private parse(value: string): number | null {
    const cleaned = value.replace(/\s/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }

  private formatDisplay(value: number | null): string {
    if (value == null) return '';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
}
